import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ExecutionResult, LcsApiService } from '@lcs/shared/api';
import { CallDetailInput, OrderingDirections, OrderingOption, Providers } from 'generated/graphql-types';
import { catchError, forkJoin, map, Observable, of, switchMap } from 'rxjs';
import { accountActiveCallsRoute, accountCallHistoryRoute, activeCallsRoute } from 'src/app/app-routing.module';
import { isValidCSharpInt } from 'src/app/common-components/helpers/csharp-max-integer-helper';
import { getProviderNumericId } from 'src/app/common-components/helpers/provider-helper';
import { FilterExpression, FilterOption, serializeFilterExpression } from 'src/app/filters/filter-definition';
import { FilterOperations } from 'src/app/filters/filter-operation/filter-operations.enum';
import { FilterPrimitive } from 'src/app/filters/filter-primitives';
import { LogicalOperators } from 'src/app/filters/logical-operators.enum';
import { DateTimeFilterFormatPipe } from 'src/app/pipes/datetime-filter-format-pipe/datetime-filter-format-pipe';
import { ActiveCallsFilterOptions } from '../models/active-calls-filter-option';
import { ActiveCallsOrderingOptions, ActiveCallsRegisterOptions } from '../models/active-calls-register-options';
import { CallDetailSaveModel } from '../models/call-detail-save.model';
import { getActiveCallsQuery } from '../queries/activecalls.graphql';
import {
   getActiveCallSegmentsAndRecordings,
   getActiveCallSegmentsAndRecordingsForQueueCallDetails,
} from '../queries/activecallsegments-and-recordings.graphql';
import { getCallDetailsReadOnlyQuery } from '../queries/calldetails.graphql';
import { getCallSegmentsAndRecordings, getRelatedCallDetail } from '../queries/callsegments-and-recordings.graphql';
import { GetActiveCalls, GetActiveCallsVariables } from '../queries/generated/GetActiveCalls';
import {
   GetActiveCallSegmentsAndRecordings,
   GetActiveCallSegmentsAndRecordingsVariables,
} from '../queries/generated/GetActiveCallSegmentsAndRecordings';
import { GetActiveCallSegmentsAndRecordingsForQueueCallDetails } from '../queries/generated/GetActiveCallSegmentsAndRecordingsForQueueCallDetails';
import { GetActiveCallsReadOnly } from '../queries/generated/GetActiveCallsReadOnly';
import { GetCallDetails } from '../queries/generated/GetCallDetails';
import { GetCallDetailsReadOnly, GetCallDetailsReadOnlyVariables } from '../queries/generated/GetCallDetailsReadOnly';
import {
   GetCallSegmentsAndRecordings,
   GetCallSegmentsAndRecordingsVariables,
} from '../queries/generated/GetCallSegmentsAndRecordings';
import { GetRelatedCallDetail, GetRelatedCallDetailVariables } from '../queries/generated/GetRelatedCallDetail';
import { SaveCallDetail, SaveCallDetailVariables } from '../queries/generated/SaveCallDetail';
import { SaveCallDetailMutation } from '../queries/save-calldetails.graphql';

const UNPAGED_ACTIVE_CALLS_PAGE_SIZE = 5000;
const PAGED_CALL_HISTORY_PAGE_SIZE = 50;

@Injectable({ providedIn: 'root' })
export class ActiveCallsRegisterApiService {
   private dateTimePipe: DateTimeFilterFormatPipe;

   constructor(private apiService: LcsApiService, private http: HttpClient) {
      this.dateTimePipe = new DateTimeFilterFormatPipe();
   }

   getActiveCalls(
      endCursor: string | null,
      filters: ActiveCallsFilterOptions[] | null,
      quickFilters: ActiveCallsRegisterOptions | null,
      orderingOptions: ActiveCallsOrderingOptions[] | null,
      currentRoute: string
   ): Observable<
      | ExecutionResult<GetActiveCalls | GetActiveCallsReadOnly>
      | ExecutionResult<GetCallDetails | GetCallDetailsReadOnly>
   > {
      const filterBuilderFilterExpression: FilterExpression | null =
         filters && filters.length > 0
            ? {
                 filterOptions: filters.map(this.convertUserFilterOption.bind(this)),
              }
            : { filterOptions: [] };
      filterBuilderFilterExpression.subExpressions = [];
      if (quickFilters) {
         if (currentRoute == 'activecalls') {
            if (quickFilters.account?.accountId) {
               filterBuilderFilterExpression.filterOptions.push({
                  field: 'Account.AccountId',
                  operation: FilterOperations.EqualTo,
                  value: quickFilters.account?.accountId,
               });
            }
         }
         if (quickFilters.providerId) {
            const providerId = this.providerToInt(quickFilters.providerId);
            filterBuilderFilterExpression.subExpressions.push(this.buildProviderFilter(providerId));
         }
         if (currentRoute == 'accountcallhistory') {
            if (quickFilters.startDate) {
               const startUtc = new Date(quickFilters.startDate).toISOString();
               filterBuilderFilterExpression.filterOptions.push({
                  field: 'startTime',
                  operation: FilterOperations.GreaterThanOrEqualTo,
                  value: this.dateTimePipe.transformUTC(startUtc),
               });
            }
            if (quickFilters.endDate) {
               const endExclusive = new Date(quickFilters.endDate);
               endExclusive.setHours(0, 0, 0, 0);
               endExclusive.setDate(endExclusive.getDate() + 1);
               filterBuilderFilterExpression.filterOptions.push({
                  field: 'startTime',
                  operation: FilterOperations.LessThan,
                  value: this.dateTimePipe.transformUTC(endExclusive),
               });
            }
            if (quickFilters.callDirection) {
               filterBuilderFilterExpression.filterOptions.push({
                  field: 'callDirectionTypeId',
                  operation: FilterOperations.EqualTo,
                  value: quickFilters.callDirection,
               });
            }

            if (quickFilters.callDisposition) {
               filterBuilderFilterExpression.filterOptions.push({
                  field: 'callDispositionTypeId',
                  operation: FilterOperations.EqualTo,
                  value: quickFilters.callDisposition,
               });
            }
            if (quickFilters.extensionGroupExtensions) {
               const subExpression: FilterExpression = { filterOptions: [], logicalOperator: LogicalOperators.Or };

               for (const item of quickFilters.extensionGroupExtensions) {
                  subExpression.filterOptions.push({
                     field: 'SourceExtension.ExtensionId',
                     operation: FilterOperations.EqualTo,
                     value: item,
                  });
                  subExpression.filterOptions.push({
                     field: 'DestinationExtension.ExtensionId',
                     operation: FilterOperations.EqualTo,
                     value: item,
                  });
               }

               filterBuilderFilterExpression.subExpressions.push(subExpression);
            }
         }

         if (quickFilters.searchText) {
            const searchText = quickFilters.searchText.trim().replace(/-/g, '');
            const extensionIdOrNumberRegex = /^\d+$/;

            const numericSearchText = searchText.match(extensionIdOrNumberRegex)?.[0];

            const subExpression: FilterExpression = { filterOptions: [], logicalOperator: LogicalOperators.Or };

            subExpression.filterOptions.push({
               field: 'SourceExtension.Name',
               operation: FilterOperations.Contains,
               value: searchText,
            });
            subExpression.filterOptions.push({
               field: 'DestinationExtension.Name',
               operation: FilterOperations.Contains,
               value: searchText,
            });
            subExpression.filterOptions.push({
               field: 'Account.Name',
               operation: FilterOperations.Contains,
               value: searchText,
            });
            subExpression.filterOptions.push({
               field: 'serverName',
               operation: FilterOperations.Contains,
               value: searchText,
            });
            if (numericSearchText) {
               subExpression.filterOptions.push({
                  field: 'destinationNumber',
                  operation: FilterOperations.Contains,
                  value: numericSearchText,
               });
               subExpression.filterOptions.push({
                  field: 'sourceNumber',
                  operation: FilterOperations.Contains,
                  value: numericSearchText,
               });
               if (isValidCSharpInt(+searchText) && numericSearchText) {
                  subExpression.filterOptions.push({
                     field: 'DestinationExtension.Number',
                     operation: FilterOperations.Contains,
                     value: numericSearchText,
                  });
                  subExpression.filterOptions.push({
                     field: 'originalDestinationNumber',
                     operation: FilterOperations.Contains,
                     value: numericSearchText,
                  });
                  subExpression.filterOptions.push({
                     field: 'DestinationExtension.extensionId',
                     operation: FilterOperations.Contains,
                     value: numericSearchText,
                  });
                  subExpression.filterOptions.push({
                     field: 'SourceExtension.Number',
                     operation: FilterOperations.Contains,
                     value: numericSearchText,
                  });
                  subExpression.filterOptions.push({
                     field: 'sourceExtension.extensionId',
                     operation: FilterOperations.Contains,
                     value: numericSearchText,
                  });
               }
            }
            filterBuilderFilterExpression.subExpressions.push(subExpression);
         }
      }

      const variables = this.getActiveCallsVariables(
         endCursor,
         filterBuilderFilterExpression,
         orderingOptions,
         currentRoute
      );
      if (currentRoute === accountCallHistoryRoute) {
         return this.apiService.query<GetCallDetailsReadOnly, GetCallDetailsReadOnlyVariables>({
            query: getCallDetailsReadOnlyQuery,
            variables,
         });
      } else {
         return this.apiService.query<GetActiveCalls, GetActiveCallsVariables>({
            query: getActiveCallsQuery,
            variables,
         });
      }
   }

   getCallDetailDialogData(callDetailId: number | null): Observable<ExecutionResult<GetCallSegmentsAndRecordings>> {
      return this.apiService
         .query<GetCallSegmentsAndRecordings, GetCallSegmentsAndRecordingsVariables>({
            query: getCallSegmentsAndRecordings,
            variables: {
               id: callDetailId,
            },
         })
         .pipe(
            switchMap((result) =>
               this.appendRelatedCallDetails(
                  result,
                  result.data.callDetail.queueHistoryCallDetailsConnection.items,
                  callDetailId
               )
            )
         );
   }

   getActiveCallDetailDialogData(
      callDetailId: number | null
   ): Observable<ExecutionResult<GetActiveCallSegmentsAndRecordings>> {
      return this.apiService
         .query<GetActiveCallSegmentsAndRecordings, GetActiveCallSegmentsAndRecordingsVariables>({
            query: getActiveCallSegmentsAndRecordings,
            variables: {
               id: callDetailId,
            },
         })
         .pipe(
            switchMap((result) =>
               this.appendRelatedCallDetails(
                  result,
                  result.data.activeCall.queueHistoryCallDetailsConnection.items,
                  callDetailId
               )
            )
         );
   }

   getRelatedCallDetailForCallback(
      callDetailId: number | null
   ): Observable<ExecutionResult<GetRelatedCallDetail | undefined>> {
      return this.apiService
         .query<GetRelatedCallDetail, GetRelatedCallDetailVariables>({
            query: getRelatedCallDetail,
            variables: {
               id: callDetailId,
            },
         })
         .pipe(catchError(() => of({ data: undefined, errors: undefined })));
   }

   getActiveCallDetailDialogDataForQueueHistory(
      callDetailId: number | null
   ): Observable<ExecutionResult<GetActiveCallSegmentsAndRecordingsForQueueCallDetails>> {
      return this.apiService
         .query<GetActiveCallSegmentsAndRecordingsForQueueCallDetails, GetActiveCallSegmentsAndRecordingsVariables>({
            query: getActiveCallSegmentsAndRecordingsForQueueCallDetails,
            variables: {
               id: callDetailId,
            },
         })
         .pipe(
            switchMap((result) =>
               this.appendRelatedCallDetails(
                  result,
                  result.data.activeCall.queueHistoryCallDetailsConnection.items,
                  callDetailId
               )
            )
         );
   }

   private appendRelatedCallDetails<T>(
      result: T,
      items: { queueHistory: { queueHistoryCallDetailsConnection?: { items: { callDetailId: number }[] } } }[],
      currentCallDetailId: number | null
   ): Observable<T & { relatedCallDetails: any[] }> {
      const relatedCallDetailIds = items
         .reduce<{ callDetailId: number }[]>(
            (acc, qh) => [...acc, ...(qh.queueHistory.queueHistoryCallDetailsConnection?.items ?? [])],
            []
         )
         .filter((qhcd) => qhcd.callDetailId !== currentCallDetailId)
         .map((qhcd) => qhcd.callDetailId)
         .filter((id, index, self) => self.indexOf(id) === index);

      if (!relatedCallDetailIds.length) {
         return of({ ...result, relatedCallDetails: [] });
      }

      return forkJoin(relatedCallDetailIds.map((id) => this.getRelatedCallDetailForCallback(id))).pipe(
         map((relatedResults) => ({
            ...result,
            relatedCallDetails: relatedResults.map((r) => r.data?.callDetail).filter(Boolean),
         }))
      );
   }

   private convertUserFilterOption(option: ActiveCallsFilterOptions): FilterOption {
      return {
         field: option.field,
         operation: option.operation,
         value: this.getFilterOptionValue(option),
      };
   }

   private getFilterOptionValue(option: ActiveCallsFilterOptions): FilterPrimitive | FilterPrimitive[] | null {
      if (option.operation === FilterOperations.HasValue) {
         return option.value;
      }
      switch (option.type) {
         case 'activeCalls':
            return Array.isArray(option.value)
               ? option.value.map((v) => {
                    return v.callDetailId;
                 })
               : option.value?.callDetailId || null;
         case 'text-box':
         case 'date':
         case 'numeric':
         case 'boolean':
            return option.value;
      }
   }

   private getActiveCallsVariables(
      after: string | null,
      filterBuilderFilters: FilterExpression | null,
      orderingOptions: OrderingOption[] | null,
      currentRoute: string
   ): GetActiveCallsVariables {
      const isActiveCallsRoute = currentRoute === activeCallsRoute || currentRoute === accountActiveCallsRoute;

      const variables: GetActiveCallsVariables = {
         after,
         filters: this.buildFilterExpression(filterBuilderFilters),
         first: isActiveCallsRoute ? UNPAGED_ACTIVE_CALLS_PAGE_SIZE : PAGED_CALL_HISTORY_PAGE_SIZE,
         orderingOptions: orderingOptions?.filter((opt) => opt.direction !== OrderingDirections.NotSet) ?? null,
      };

      return variables;
   }

   private buildFilterExpression(filterBuilderFilters: FilterExpression | null): string {
      const expressions: FilterExpression[] = filterBuilderFilters?.subExpressions ?? [];

      if (filterBuilderFilters) {
         return serializeFilterExpression({
            filterOptions: filterBuilderFilters.filterOptions,
            logicalOperator: LogicalOperators.And,
            subExpressions: expressions,
         });
      }

      if (expressions.length > 1) {
         return serializeFilterExpression({
            filterOptions: [],
            logicalOperator: LogicalOperators.And,
            subExpressions: expressions,
         });
      } else if (expressions.length === 1) {
         return serializeFilterExpression(expressions[0]);
      } else {
         return '';
      }
   }

   private buildProviderFilter(providerId: number): FilterExpression {
      return {
         filterOptions: [
            {
               field: 'sourceProviderId',
               operation: FilterOperations.EqualTo,
               value: providerId,
            },
            {
               field: 'destinationProviderId',
               operation: FilterOperations.EqualTo,
               value: providerId,
            },
         ],
         logicalOperator: LogicalOperators.Or,
         subExpressions: [
            {
               filterOptions: [
                  {
                     field: 'providerId',
                     operation: FilterOperations.EqualTo,
                     value: providerId,
                  },
                  {
                     field: 'sourceProviderId',
                     operation: FilterOperations.HasValue,
                     value: false,
                  },
                  {
                     field: 'destinationProviderId',
                     operation: FilterOperations.HasValue,
                     value: false,
                  },
               ],
               logicalOperator: LogicalOperators.And,
            },
         ],
      };
   }

   providerToInt(provider: Providers): number {
      return getProviderNumericId(provider);
   }

   save(
      callDetailDetail: CallDetailSaveModel,
      fields: (keyof CallDetailInput)[]
   ): Observable<ExecutionResult<SaveCallDetail>> {
      return this.apiService.query<SaveCallDetail, SaveCallDetailVariables>({
         query: SaveCallDetailMutation,
         variables: {
            callDetail: {
               ...callDetailDetail,
               fields: fields,
            },
         },
      });
   }

   hangupActiveCall(serverIp: string | null, channelId: string | null, callDetailId: any | null): Observable<any> {
      const httpOptions = {
         headers: new HttpHeaders({
            'Content-Type': 'application/json',
         }),
      };
      return this.http.post(
         this.apiService.buildApiUrl('call/hangupcall'),
         JSON.stringify({ channelId: channelId ?? '', serverIp: serverIp ?? '', callDetailId: callDetailId }),
         httpOptions
      );
   }

   checkIfCallActive(serverIp: string | null, channelId: string | null, callDetailId: any | null): Observable<any> {
      const httpOptions = {
         headers: new HttpHeaders({
            'Content-Type': 'application/json',
         }),
      };
      return this.http.post(
         this.apiService.buildApiUrl('call/isCallActive'),
         JSON.stringify({ channelId: channelId ?? '', serverIp: serverIp ?? '', callDetailId: callDetailId }),
         httpOptions
      );
   }

   sendCallToEmptyExitRoute(serverName: string | null, sipCallId: string | null): Observable<any> {
      const httpOptions = {
         headers: new HttpHeaders({
            'Content-Type': 'application/json',
         }),
      };
      return this.http.post(
         this.apiService.buildApiUrl('call/routeQueueCallToEmptyExitRoute'),
         JSON.stringify({ serverName: serverName ?? '', sipCallId: sipCallId ?? '' }),
         httpOptions
      );
   }
}
